import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import SubscriptionModal from './SubscriptionModal';
import Header from './Header';
import useDarkMode from '../hooks/useDarkMode';
import useSocialUser from '../hooks/useSocialUser';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8000';

function fmtKRW(n) {
  if (typeof n !== 'number') return '-';
  return n.toLocaleString('ko-KR') + '원';
}

function fmtDate(iso) {
  if (!iso) return '-';
  const d = new Date(iso);
  return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
}

const PAYMENT_STATUS_LABEL = {
  done: '결제완료',
  canceled: '환불',
  failed: '실패',
  partial_canceled: '부분환불',
};

function paymentStatusStyle(status, dark) {
  if (status === 'done') return dark
    ? { background: '#0f3d1f', color: '#4ade80' }
    : { background: '#e6f7ed', color: '#1e7a3d' };
  if (status === 'canceled' || status === 'failed') return dark
    ? { background: '#3d0f0f', color: '#f87171' }
    : { background: '#fdecec', color: '#c62b2b' };
  if (status === 'partial_canceled') return dark
    ? { background: '#3d1f0f', color: '#fb923c' }
    : { background: '#fdf1e2', color: '#b45309' };
  return {};
}

export default function MyPage() {
  const navigate = useNavigate();
  const [darkMode] = useDarkMode();
  const socialUser = useSocialUser();
  const uid = socialUser?.uid || null;

  const [subscription, setSubscription] = useState(null);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [cancelBusy, setCancelBusy] = useState(false);
  const [showResubModal, setShowResubModal] = useState(false);
  const [toast, setToast] = useState('');

  useEffect(() => {
    if (!uid) navigate('/', { replace: true });
  }, [uid, navigate]);

  const fetchAll = useCallback(async () => {
    if (!uid) return;
    setLoading(true);
    setError('');
    try {
      const [subRes, payRes] = await Promise.all([
        fetch(`${API_BASE}/billing/status?userId=${uid}`).then(r => r.json()),
        fetch(`${API_BASE}/billing/history?userId=${uid}`).then(r => r.json()),
      ]);
      setSubscription(subRes.subscription || null);
      setPayments(payRes.payments || []);
    } catch (e) {
      setError(String(e));
    } finally {
      setLoading(false);
    }
  }, [uid]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleCancel = async () => {
    if (!window.confirm('구독을 해지하시겠어요?\n\n다음 결제일 전까지는 계속 이용할 수 있고, 이후 자동으로 무료 플랜으로 전환됩니다.')) return;
    setCancelBusy(true);
    try {
      const res = await fetch(`${API_BASE}/billing/cancel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: uid }),
      });
      const data = await res.json();
      if (data.ok) {
        setToast('구독이 해지됐어요.');
        setTimeout(() => setToast(''), 2500);
        fetchAll();
      } else {
        setToast(`실패: ${data.error || '알 수 없는 오류'}`);
        setTimeout(() => setToast(''), 3500);
      }
    } catch (e) {
      setToast(`네트워크 오류: ${String(e)}`);
      setTimeout(() => setToast(''), 3500);
    } finally {
      setCancelBusy(false);
    }
  };

  const statusInfo = getStatusInfo(subscription);
  const styles = useMemo(() => makeStyles(darkMode), [darkMode]);

  return (
    <div style={styles.page}>
      <Header />
      <div style={styles.container}>
        <h1 style={styles.title}>마이페이지</h1>

        {loading && <div style={styles.loading}>불러오는 중...</div>}
        {error && <div style={styles.error}>에러: {error}</div>}

        {!loading && (
          <>
            <section style={{ ...styles.card, borderTop: `3px solid ${statusInfo.accent}` }}>
              <div style={styles.cardHeader}>
                <div>
                  <div style={styles.cardLabel}>구독 상태</div>
                  <div style={{ ...styles.cardStatusText, color: statusInfo.accent }}>
                    {statusInfo.label}
                  </div>
                </div>
                {statusInfo.showManage && (
                  <button
                    onClick={handleCancel}
                    disabled={cancelBusy}
                    style={styles.cancelBtn}
                  >
                    {cancelBusy ? '처리 중...' : '구독 해지'}
                  </button>
                )}
                {statusInfo.showResub && (
                  <button
                    onClick={() => setShowResubModal(true)}
                    style={styles.resubBtn}
                  >
                    다시 구독하기
                  </button>
                )}
              </div>
              <div style={styles.cardBody}>
                {statusInfo.rows.map((r, i) => (
                  <div key={i} style={styles.cardRow}>
                    <span style={styles.cardRowLabel}>{r.label}</span>
                    <span style={styles.cardRowValue}>{r.value}</span>
                  </div>
                ))}
              </div>
              {statusInfo.note && (
                <div style={styles.cardNote}>{statusInfo.note}</div>
              )}
            </section>

            <section style={styles.card}>
              <div style={styles.cardLabel}>결제 내역</div>
              <div style={styles.tableWrap}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>날짜</th>
                      <th style={styles.thRight}>금액</th>
                      <th style={styles.th}>상태</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments.length === 0 && (
                      <tr><td colSpan={3} style={styles.empty}>결제 내역이 없어요</td></tr>
                    )}
                    {payments.map(p => (
                      <tr key={p.id} style={styles.tr}>
                        <td style={styles.td}>{fmtDate(p.paid_at)}</td>
                        <td style={styles.tdRight}>{fmtKRW(p.amount)}</td>
                        <td style={styles.td}>
                          <span style={{ ...styles.statusBadge, ...paymentStatusStyle(p.status, darkMode) }}>
                            {PAYMENT_STATUS_LABEL[p.status] || p.status}
                          </span>
                          {p.status === 'failed' && p.failure_code && (
                            <span style={styles.failCode}>({p.failure_code})</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={styles.hint}>최근 50건까지 표시</div>
            </section>
          </>
        )}
      </div>

      {toast && <div style={styles.toast}>{toast}</div>}

      {showResubModal && uid && (
        <SubscriptionModal
          userId={uid}
          darkMode={darkMode}
          onClose={() => setShowResubModal(false)}
          onSuccess={() => { setShowResubModal(false); fetchAll(); }}
        />
      )}
    </div>
  );
}

function getStatusInfo(sub) {
  if (!sub) {
    return {
      label: '구독 안 함',
      accent: '#888',
      rows: [{ label: '플랜', value: '무료' }],
      note: '무료 플랜에서는 일부 레시피가 제한됩니다.',
      showResub: true,
    };
  }
  const { status, trial_ends_at, next_billing_at, canceled_at, created_at } = sub;
  const now = new Date();

  if (status === 'trialing') {
    const trialEnd = trial_ends_at ? new Date(trial_ends_at) : null;
    const stillTrialing = trialEnd && now < trialEnd;
    if (stillTrialing) {
      return {
        label: '무료 체험 중',
        accent: '#3182f6',
        rows: [
          { label: '플랜', value: 'Premium (체험)' },
          { label: '체험 시작', value: fmtDate(created_at) },
          { label: '체험 종료', value: fmtDate(trial_ends_at) },
          { label: '자동 결제 시작', value: fmtDate(next_billing_at) },
        ],
        note: '체험 종료 시 월 4,900원이 자동 결제됩니다. 언제든 해지할 수 있어요.',
        showManage: true,
      };
    }
  }

  if (status === 'active') {
    return {
      label: '구독 중',
      accent: '#22c55e',
      rows: [
        { label: '플랜', value: 'Premium (월 4,900원)' },
        { label: '구독 시작', value: fmtDate(created_at) },
        { label: '다음 결제일', value: fmtDate(next_billing_at) },
      ],
      showManage: true,
    };
  }

  if (status === 'cancelled') {
    const nextBilling = next_billing_at ? new Date(next_billing_at) : null;
    const stillActive = nextBilling && now < nextBilling;
    return {
      label: stillActive ? '해지 예약됨' : '해지됨',
      accent: '#fb923c',
      rows: [
        { label: '플랜', value: stillActive ? 'Premium (해지 예약)' : '무료' },
        { label: '해지일', value: fmtDate(canceled_at) },
        ...(stillActive ? [{ label: '이용 종료', value: fmtDate(next_billing_at) }] : []),
      ],
      note: stillActive
        ? `${fmtDate(next_billing_at)}까지 계속 이용할 수 있어요.`
        : '무료 플랜으로 전환됐어요.',
      showResub: true,
    };
  }

  if (status === 'payment_failed') {
    return {
      label: '결제 실패',
      accent: '#ef4444',
      rows: [
        { label: '플랜', value: 'Premium (결제 실패)' },
        { label: '마지막 시도', value: fmtDate(next_billing_at) },
      ],
      note: '카드를 다시 등록해 주세요.',
      showResub: true,
    };
  }

  return {
    label: status || '알 수 없음',
    accent: '#888',
    rows: [{ label: '상태', value: status || '-' }],
  };
}

function makeStyles(dark) {
  const bg = dark ? '#111' : '#f5f6f8';
  const text = dark ? '#e0e0e0' : '#1a1a1a';
  const cardBg = dark ? '#1a1a1a' : '#ffffff';
  const cardBorder = dark ? '#2a2a2a' : '#e5e7eb';
  const rowBorder = dark ? '#222' : '#f0f2f5';
  const subText = dark ? 'rgba(224,224,224,0.7)' : '#6b7280';
  const noteBg = dark ? '#141414' : '#f8f9fb';
  const errBg = dark ? '#3d0f0f' : '#fdecec';
  const errText = dark ? '#f87171' : '#c62b2b';
  const toastBg = dark ? '#333' : '#222';

  return {
    page: { minHeight: '100vh', background: bg, color: text, boxSizing: 'border-box' },
    container: { maxWidth: 720, margin: '0 auto', padding: '24px 12px' },
    title: { fontSize: 24, fontWeight: 700, margin: '0 0 20px', color: text },
    loading: { padding: 40, textAlign: 'center', color: subText },
    error: { padding: 12, background: errBg, color: errText, borderRadius: 6, marginBottom: 12 },
    card: { background: cardBg, border: `1px solid ${cardBorder}`, borderRadius: 8, padding: 20, marginBottom: 16 },
    cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, gap: 12, flexWrap: 'wrap' },
    cardLabel: { fontSize: 12, color: subText, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' },
    cardStatusText: { fontSize: 22, fontWeight: 700 },
    cardBody: { display: 'flex', flexDirection: 'column', gap: 8 },
    cardRow: { display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: `1px solid ${rowBorder}` },
    cardRowLabel: { color: subText, fontSize: 14 },
    cardRowValue: { fontSize: 14, fontWeight: 500, color: text },
    cardNote: { marginTop: 12, padding: 10, background: noteBg, borderRadius: 6, fontSize: 12, color: subText, lineHeight: 1.5 },
    cancelBtn: { padding: '8px 16px', background: 'transparent', color: '#ef4444', border: '1px solid #ef4444', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 500 },
    resubBtn: { padding: '8px 16px', background: '#3182f6', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600 },
    tableWrap: { overflowX: 'auto', marginTop: 12 },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
    th: { textAlign: 'left', padding: '10px 8px', borderBottom: `1px solid ${cardBorder}`, fontWeight: 600, color: subText, fontSize: 12 },
    thRight: { textAlign: 'right', padding: '10px 8px', borderBottom: `1px solid ${cardBorder}`, fontWeight: 600, color: subText, fontSize: 12 },
    tr: { borderBottom: `1px solid ${rowBorder}` },
    td: { padding: '12px 8px', color: text },
    tdRight: { padding: '12px 8px', textAlign: 'right', color: text },
    statusBadge: { display: 'inline-block', padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600 },
    failCode: { marginLeft: 8, fontSize: 11, color: subText },
    empty: { textAlign: 'center', padding: 40, color: subText },
    hint: { fontSize: 12, color: subText, marginTop: 12 },
    toast: { position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', background: toastBg, color: '#fff', padding: '10px 20px', borderRadius: 20, fontSize: 13, boxShadow: '0 4px 12px rgba(0,0,0,0.3)' },
  };
}
